/**
 * CompleteGoogleSignup
 * Consent gate for new Google OAuth users.
 * Shown once after first Google sign-in to capture phone, T&C, and SMS consent.
 * Users cannot reach the dashboard until this is completed.
 */

import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '@contexts/AuthContext'
import { supabase } from '@lib/supabase'
import { useForm } from 'react-hook-form'
import { PhoneIcon } from 'lucide-react'
import Button from '@components/ui/Button'
import Input from '@components/ui/Input'
import BrandLogo from '@components/BrandLogo'
import SmsEmailOptIn from '@components/forms/SmsEmailOptIn'
import toast from 'react-hot-toast'

const CompleteGoogleSignup = () => {
  const navigate = useNavigate()
  const { fetchUserData } = useAuth()
  const [loading, setLoading] = useState(false)
  const [initializing, setInitializing] = useState(true)
  const [sessionUser, setSessionUser] = useState(null)
  const [transactionalConsent, setTransactionalConsent] = useState(false)
  const [marketingConsent, setMarketingConsent] = useState(false)

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm()

  useEffect(() => {
    const init = async () => {
      const { data: { session } } = await supabase.auth.getSession()

      if (!session) {
        // Not authenticated — send to login
        navigate('/login', { replace: true })
        return
      }

      // Guard: if user already completed consent, send them to their dashboard
      const { data: userRecord } = await supabase
        .from('users')
        .select('sms_transactional_consent, role, phone')
        .eq('id', session.user.id)
        .maybeSingle()

      if (
        userRecord &&
        userRecord.phone !== null &&
        userRecord.phone !== undefined
      ) {
        // Already completed — redirect to appropriate dashboard
        if (userRecord.role === 'photographer') {
          navigate('/talent/dashboard', { replace: true })
        } else if (userRecord.role === 'admin') {
          navigate('/lp-console', { replace: true })
        } else {
          navigate('/dashboard', { replace: true })
        }
        return
      }

      setSessionUser(session.user)

      // Pre-fill name from Google metadata if available
      const googleName =
        session.user.user_metadata?.full_name ||
        session.user.user_metadata?.name ||
        ''
      if (googleName) {
        setValue('fullName', googleName)
      }

      setInitializing(false)
    }

    init()
  }, [navigate, setValue])

  const onSubmit = async (data) => {
    if (!transactionalConsent) {
      toast.error('Please agree to receive notifications to continue')
      return
    }

    if (!sessionUser) {
      toast.error('Session expired. Please sign in again.')
      navigate('/login', { replace: true })
      return
    }

    setLoading(true)
    try {
      const phoneDigits = data.phone ? data.phone.replace(/\D/g, '') : null

      const { error } = await supabase
        .from('users')
        .update({
          full_name: data.fullName || '',
          phone: phoneDigits,
          sms_transactional_consent: transactionalConsent,
          sms_marketing_consent: marketingConsent,
          updated_at: new Date().toISOString()
        })
        .eq('id', sessionUser.id)

      if (error) throw error

      // Refresh auth context so profile is up-to-date
      await fetchUserData(sessionUser)

      toast.success('Account setup complete! Welcome to Love & Photos!')
      navigate('/dashboard', { replace: true })
    } catch (error) {
      console.error('[CompleteGoogleSignup] Error saving consent:', error)
      toast.error('Something went wrong. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  if (initializing) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500" />
      </div>
    )
  }

  const googleEmail = sessionUser?.email || ''
  const hasGoogleName = !!(
    sessionUser?.user_metadata?.full_name || sessionUser?.user_metadata?.name
  )

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <Link to="/">
              <BrandLogo size="lg" variant="icon" showText={false} />
            </Link>
          </div>
          <h2 className="text-3xl font-display font-bold text-foreground">
            One last step
          </h2>
          <p className="mt-2 text-muted-foreground">
            Complete your account setup to continue
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="bg-card rounded-xl shadow-sm border border-border p-6 space-y-4">
            {/* Name — pre-filled from Google, editable if not provided */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Full Name
              </label>
              <input
                type="text"
                readOnly={hasGoogleName}
                {...register('fullName', { required: 'Full name is required' })}
                className={[
                  'w-full px-3 py-2 border border-border rounded-lg text-sm text-foreground',
                  hasGoogleName
                    ? 'bg-muted cursor-default'
                    : 'bg-background focus:outline-none focus:ring-2 focus:ring-primary'
                ].join(' ')}
                placeholder="Your full name"
              />
              {errors.fullName && (
                <p className="text-sm text-red-600 mt-1">{errors.fullName.message}</p>
              )}
              {hasGoogleName && (
                <p className="text-xs text-muted-foreground mt-1">
                  Filled in from your Google account
                </p>
              )}
            </div>

            {/* Email — always read-only from Google */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Email Address
              </label>
              <input
                type="email"
                value={googleEmail}
                readOnly
                className="w-full px-3 py-2 border border-border rounded-lg bg-muted text-foreground text-sm cursor-not-allowed"
              />
              <p className="text-xs text-muted-foreground mt-1">
                From your Google account
              </p>
            </div>

            {/* Phone — required, not available from Google */}
            <Input
              label="Phone Number"
              type="tel"
              icon={<PhoneIcon className="w-5 h-5 text-muted-foreground" />}
              {...register('phone', {
                required: 'Phone number is required',
                validate: (value) => {
                  if (!value) return 'Phone number is required'
                  const digits = value.replace(/\D/g, '')
                  return digits.length === 10 || 'Enter a valid 10-digit phone number'
                }
              })}
              placeholder="(555) 123-4567"
              error={errors.phone?.message}
            />
          </div>

          {/* Terms of Service — required */}
          <div className="flex items-start">
            <input
              type="checkbox"
              id="terms"
              {...register('terms', {
                required: 'You must accept the Terms of Service to continue'
              })}
              className="mt-1 h-4 w-4 text-primary border-border rounded focus:ring-primary"
            />
            <label htmlFor="terms" className="ml-2 text-sm text-muted-foreground">
              I agree to the{' '}
              <Link to="/terms" className="text-primary hover:text-primary/80" target="_blank">
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link to="/privacy" className="text-primary hover:text-primary/80" target="_blank">
                Privacy Policy
              </Link>
            </label>
          </div>
          {errors.terms && (
            <p className="text-sm text-red-600 -mt-4">{errors.terms.message}</p>
          )}

          {/* SMS / Email consent — transactional required, marketing optional */}
          <SmsEmailOptIn
            transactionalConsent={transactionalConsent}
            onTransactionalChange={setTransactionalConsent}
            marketingConsent={marketingConsent}
            onMarketingChange={setMarketingConsent}
          />

          <Button
            type="submit"
            loading={loading}
            disabled={loading}
            className="w-full"
            size="lg"
          >
            Complete Setup
          </Button>
        </form>
      </div>
    </div>
  )
}

export default CompleteGoogleSignup
